import { ClipType } from "./types";

// Duplicate-clip grouping for the Media Matrix.
//
// Clips produced by the same record automation (one camera, its retries) form a "duplicate group",
// keyed on {EventRecordId, AutomationId}. Clips with no automation id are standalone (each its own
// singleton group). The "best" clip of a group is chosen deterministically (FR-004) and shown by
// default; the remainder are its nested duplicates.

/** Group key for a clip, or null when it has no automation id (standalone). */
export function automationGroupKey(clip: ClipType): string | null {
	if (clip.AutomationId === null || clip.AutomationId === undefined) {
		return null;
	}
	return `${clip.EventRecordId}:${clip.AutomationId}`;
}

function completenessRank(clip: ClipType): number {
	// Lower is better. Completion status is tri-state (FR-004 step 1, SC-007): a known-complete clip
	// wins, an unknown one sits behind it but no lower than partial, and partial ranks last. Unknown
	// must never rank as complete, so null/undefined cannot collapse into the complete bucket.
	if (clip.IsPartial === true) {
		return 2; // partial
	}
	if (clip.IsPartial === false) {
		return 0; // complete
	}
	return 1; // unknown — status unavailable, never asserted complete
}

/**
 * Parse a clip time value to epoch milliseconds. Handles ISO strings, Date objects, and the
 * Microsoft JSON date format ("/Date(1234567890000)/") that SVAudit's JavaScriptSerializer emits —
 * which `new Date(...)` cannot parse (it would yield NaN and silently break ordering).
 */
export function clipTimeMs(raw: string | Date | null | undefined): number {
	if (raw === null || raw === undefined) {
		return 0;
	}
	if (raw instanceof Date) {
		const dt = raw.getTime();
		return isNaN(dt) ? 0 : dt;
	}
	const s = String(raw);
	const ms = s.match(/\/Date\((-?\d+)/); // Microsoft JSON date
	if (ms) {
		return parseInt(ms[1], 10);
	}
	const t = new Date(s).getTime();
	return isNaN(t) ? 0 : t;
}

/**
 * The clip's own created time. StartTime is parsed by SVAudit from the {yyyyMMdd}.{HHmmssff} tokens
 * of the clip's filename, so it is per-clip and to 1/100 s — this is what discriminates a group's
 * retry attempts. CreatedUTC is only a fallback: it is the *event record's* created time, identical
 * for every clip in the record, so it cannot separate members of a group on its own.
 */
function recordedTime(clip: ClipType): number {
	return clipTimeMs(clip.StartTime || clip.CreatedUTC);
}

function stableId(clip: ClipType): string {
	return clip.UniqueFileIdentifier || clip.FileIdentifier || "";
}

/**
 * Deterministic "best" ordering (FR-004), applied in order:
 * 1) complete → unknown → partial, 2) most recently created (newest first), 3) largest, 4) stable id.
 * Sort ascending — the best clip sorts first.
 *
 * Clip duration is deliberately not a factor: it is not carried by the clip listing (it cannot be
 * read from the filename or a file stat), and created time is recorded to 1/100 s, so a tie
 * surviving steps 1-3 is vanishingly rare — the stable id already guarantees determinism.
 */
export function compareForBest(a: ClipType, b: ClipType): number {
	if (completenessRank(a) !== completenessRank(b)) {
		return completenessRank(a) - completenessRank(b);
	}
	if (recordedTime(a) !== recordedTime(b)) {
		return recordedTime(b) - recordedTime(a); // most recently created first
	}
	const sizeA = a.FileSize || 0;
	const sizeB = b.FileSize || 0;
	if (sizeA !== sizeB) {
		return sizeB - sizeA; // largest first
	}
	// Ordinal comparison, not localeCompare: collation is locale/ICU-dependent, so the same two clips
	// could order differently between operators' browsers — the tiebreak has to be absolute.
	const idA = stableId(a);
	const idB = stableId(b);
	if (idA === idB) {
		return 0;
	}
	return idA < idB ? -1 : 1;
}

/** Group clips into duplicate groups; standalone clips each become a singleton group. */
export function groupClips(clips: ClipType[]): ClipType[][] {
	const groups = new Map<string, ClipType[]>();
	const standalone: ClipType[][] = [];
	for (const clip of clips) {
		const key = automationGroupKey(clip);
		if (key === null) {
			standalone.push([clip]);
			continue;
		}
		const existing = groups.get(key);
		if (existing) {
			existing.push(clip);
		} else {
			groups.set(key, [clip]);
		}
	}
	return [...Array.from(groups.values()), ...standalone];
}

/** The single "best" clip of a group per FR-004. */
export function pickBestClip(group: ClipType[]): ClipType {
	return group.slice().sort(compareForBest)[0];
}

/** The clips to place on the matrix by default: one best clip per group (FR-003). */
export function defaultClipsForMatrix(clips: ClipType[]): ClipType[] {
	return groupClips(clips).map(pickBestClip);
}

/** The duplicates of a clip's group (everything except the best), in best-first order. */
export function duplicatesOf(best: ClipType, clips: ClipType[]): ClipType[] {
	const key = automationGroupKey(best);
	if (key === null) {
		return [];
	}
	return clips
		.filter(c => automationGroupKey(c) === key && stableId(c) !== stableId(best))
		.sort(compareForBest);
}
